/*
 * @Author: Yun
 * @Date: 2025-09-24 13:55:25
 * @Last Modified by: Yun
 * @Last Modified time: 2025-12-31 15:49:05
 */
import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { content } from '@configs/content';
import styles from './index.module.scss';

// 声明jwplayer全局变量
declare global {
    interface Window {
        jwplayer: any;
    }
}

gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);

const Hero: React.FC = () => {
    const { firstCoverBg, backgroundImageTop, backgroundImageBottom, videoSrc, posterImage, sliceImages, sliceImagesMobile } =
        content.hero.assets;
    const { mainTitle, quote, leftSectionTitle, vip } = content.hero.text;
    const containerRef = useRef<HTMLDivElement>(null);
    const firstCoverRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);

    const personRef = useRef<HTMLImageElement>(null);
    const vipName = useRef<HTMLHeadingElement>(null);
    // const videoQuoteRef = useRef<HTMLQuoteElement>(null);
    // const heroDecorationRef = useRef<HTMLDivElement>(null);
    const videoPlayerRef = useRef<HTMLDivElement>(null);
    const videoPlayerInstanceRef = useRef<any>(null);
    const videoContainerRef = useRef<HTMLDivElement>(null);

    const openingSlicesRef = useRef<HTMLDivElement>(null);
    const [visibleSlices, setVisibleSlices] = useState<string[]>([]);

    useEffect(() => {
        const updateVisibleSlices = () => {
            const isMobile = window.innerWidth <= 768;
            setVisibleSlices(isMobile ? sliceImagesMobile : sliceImages);
        };

        updateVisibleSlices();
        window.addEventListener('resize', updateVisibleSlices);

        return () => window.removeEventListener('resize', updateVisibleSlices);
    }, [sliceImages]);

    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const images = [firstCoverBg, backgroundImageTop, backgroundImageBottom];
        let loadedCount = 0;

        const handleLoad = () => {
            loadedCount++;
            if (loadedCount === images.length) {
                setIsLoaded(true);
            }
        };

        images.forEach((src) => {
            const img = new Image();
            img.onload = handleLoad;
            img.onerror = handleLoad;
            img.src = src;
        });
    }, [firstCoverBg, backgroundImageTop, backgroundImageBottom]);

    const setJWplayer = (dom: HTMLElement, data: { video: string; poster: string }) => {
        try {
            if (window.jwplayer) {
                window.jwplayer.key = content.hero.jwplayer.key;

                const instance = window
                    .jwplayer(dom)
                    .setup({
                        file: data.video,
                        image: data.poster,
                        stretching: 'uniform',
                        width: '100%',
                        aspectratio: '16:9',
                        androidhls: true,
                        primary: 'html5',
                        autostart: false,
                        mute: false,
                        controls: true,
                        repeat: false,
                        hlshtml: true,
                        preload: 'metadata',
                        displaytitle: false,
                        displaydescription: false,
                    })
                    .on('setupError', (e: any) => {
                        console.error('JWPlayer Setup Error:', e);
                    })
                    .on('play', () => {
                        console.log('JWPlayer: video play');
                    })
                    .on('pause', () => {
                        console.log('JWPlayer: video pause');
                    })
                    .on('ready', () => {
                        console.log('JWPlayer: ready');
                    });

                return instance;
            } else {
                console.error('JWPlayer library not found');
            }
        } catch (error) {
            console.error('Setup JWplayer error:', error);
        }
        return null;
    };

    useEffect(() => {
        if (!isLoaded) return;

        const timer = setTimeout(() => {
            if (window.jwplayer && videoPlayerRef.current) {
                console.log('JWPlayer library found, initializing...');
                videoPlayerInstanceRef.current = setJWplayer(videoPlayerRef.current, {
                    video: videoSrc,
                    poster: posterImage,
                });
            } else {
                console.error('JWPlayer library not found');
            }
        }, 300);

        return () => {
            clearTimeout(timer);
            if (videoPlayerInstanceRef.current) {
                try {
                    videoPlayerInstanceRef.current.remove();
                    videoPlayerInstanceRef.current = null;
                } catch (error) {
                    console.error('Remove JWPlayer error:', error);
                }
            }
        };
    }, [videoSrc, posterImage, isLoaded]);

    useGSAP(
        () => {
            if (!isLoaded) return;

            const titleSplit = new SplitText(titleRef.current, {
                type: 'words',
                wordsClass: 'word',
            });

            /* const videoQuoteSplit = new SplitText(videoQuoteRef.current, {
                type: 'words',
                wordsClass: 'quote-word',
            }); */

            gsap.set(titleSplit.words, {
                opacity: 0,
                y: 20,
            });

            /* gsap.set(videoQuoteSplit.words, {
                opacity: 0,
                y: 10,
            }); */

            if (videoContainerRef.current) {
                videoContainerRef.current.style.pointerEvents = 'none';
                videoContainerRef.current.style.zIndex = '-1';
            }
            gsap.to(titleSplit.words, {
                opacity: 1,
                y: 0,
                duration: 0.4,
                stagger: 0.08,
                ease: 'power2.out',
                delay: 1.5,
            });

            const slices = openingSlicesRef.current?.querySelectorAll(`.${styles.slice}`) || [];
            const titleContainer = openingSlicesRef.current?.querySelector(`.${styles.sliceTitle}`);
            const isMobile = window.innerWidth <= 768;
            
            // ========== 滚动距离配置 ==========
            // 调整这些值来控制滚动速度和各阶段停留时间
            const scrollConfig = {
                // 总滚动距离（像素）- 越大滚动越慢，整体停留时间越长
                totalDistance: isMobile ? 2000 : 4500,
                
                // 时间轴各阶段的时间点（相对于总duration）
                // 开屏切片消失阶段：0 - 2
                slicesStart: 0,
                slicesDuration: 1,
                
                // 标题消失阶段：1 - 2
                titleStart: 1,
                titleDuration: 1,
                
                // 第二页（人物+视频）出现阶段：2 - 3
                secondPageStart: 2,
                secondPageDuration: 3,
            };
            
            const mainTl = gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top top',
                    end: `+=${scrollConfig.totalDistance}`,
                    scrub: 1.2,
                    pin: true,
                    anticipatePin: 1,
                },
            });
            
            // ========== 开屏切片消失动画 ==========
            if (slices.length > 0) {
                mainTl.to(
                    slices,
                    {
                        yPercent: -100,
                        xPercent: 10,
                        opacity: 0.2,
                        duration: scrollConfig.slicesDuration,
                        stagger: 0.3,
                        ease: 'power2.inOut',
                    },
                    scrollConfig.slicesStart
                );
            }

            // ========== 标题消失动画 ==========
            if (titleContainer) {
                mainTl.to(
                    titleContainer,
                    {
                        opacity: 0,
                        y: -50,
                        duration: scrollConfig.titleDuration,
                        ease: 'power2.out',
                    },
                    scrollConfig.titleStart
                );
            }

            // ========== 第二页人物出现动画 ==========
            if (personRef.current && vipName.current) {
                mainTl.fromTo(
                    personRef.current,
                    {
                        opacity: 0,
                        y: 30,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: scrollConfig.secondPageDuration,
                        ease: 'power2.out',
                    },
                    scrollConfig.secondPageStart
                );
                mainTl.fromTo(
                    vipName.current,
                    {
                        opacity: 0,
                        y: 30,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: scrollConfig.secondPageDuration,
                        ease: 'power2.out',
                    },
                    scrollConfig.secondPageStart
                );
            }

            /* if (heroDecorationRef.current) {
                mainTl.fromTo(
                    heroDecorationRef.current,
                    {
                        opacity: 0,
                        scale: 0.5,
                        rotation: -30,
                    },
                    {
                        opacity: 1,
                        scale: 1,
                        rotation: 0,
                        duration: 1.2,
                        ease: 'back.out(1.5)',
                    },
                    scrollConfig.secondPageStart
                );
            } */

            // ========== 第二页视频容器出现动画 ==========
            mainTl.fromTo(
                videoContainerRef.current,
                {
                    opacity: 0,
                    y: 50,
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: scrollConfig.secondPageDuration,
                    ease: 'power2.out',
                    onStart: () => {
                        if (videoContainerRef.current) {
                            videoContainerRef.current.style.pointerEvents = 'auto';
                            videoContainerRef.current.style.zIndex = '10';
                        }
                        /* gsap.to(videoQuoteSplit.words, {
                            opacity: 1,
                            y: 0,
                            duration: 0.3,
                            stagger: 0.05,
                            ease: 'power2.out',
                            delay: 0.3,
                        }); */
                    },
                    onReverseComplete: () => {
                        if (videoContainerRef.current) {
                            videoContainerRef.current.style.pointerEvents = 'none';
                            videoContainerRef.current.style.zIndex = '-1';
                        }
                        if (videoPlayerInstanceRef.current) {
                            try {
                                const state = videoPlayerInstanceRef.current.getState();
                                if (state === 'playing' || state === 'buffering') {
                                    videoPlayerInstanceRef.current.pause();
                                }
                            } catch (error) {}
                        }
                        /* gsap.set(videoQuoteSplit.words, {
                            opacity: 0,
                            y: 10,
                        }); */
                    },
                },
                scrollConfig.secondPageStart
            );
        },
        { scope: containerRef, dependencies: [isLoaded] }
    );

    return (
        <>
            {ReactDOM.createPortal(
                <div ref={openingSlicesRef} className={styles.openingSlices}>
                    {visibleSlices.map((img, index) => (
                        <div key={index} className={styles.slice} data-index={index + 1}>
                            <div className={styles.sliceImageWrapper}>
                                <div className={styles.sliceBackground} style={{ backgroundImage: `url(${img})` }} />
                                <img src={img} alt="" className={styles.sliceImage} />
                            </div>
                            <div className={styles.sliceGlass} />
                        </div>
                    ))}
                    <div className={styles.sliceTitle}>
                        <h1 ref={titleRef} className={styles.mainTitle}>
                            {mainTitle}
                        </h1>
                    </div>
                </div>,
                document.body
            )}

            <div ref={containerRef} className={styles.heroCovers} id="hero">
                <div ref={firstCoverRef} className={`${styles.cover} ${styles.coverFirst}`}>
                    <div className={styles.coverBgTop} style={{ backgroundImage: `url(${backgroundImageTop})` }} />
                    <div className={styles.coverBgBottom} style={{ backgroundImage: `url(${backgroundImageBottom})` }} />
                </div>

                {/* <div ref={heroDecorationRef} className={styles.heroDecoration}>
                    <svg className={styles.heroSvg} viewBox="0 0 200 200">
                        <defs>
                            <filter id="hero-fluid-filter">
                                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" />
                                <feColorMatrix values="0 0 0 0 0.1 0 0 0 0 0.1 0 0 0 0 0.2 0 0 0 0.3 0" />
                            </filter>
                        </defs>
                        <circle
                            cx="100"
                            cy="100"
                            r="80"
                            fill="none"
                            stroke="rgba(255, 255, 255, 0.6)"
                            strokeWidth="2"
                            filter="url(#hero-fluid-filter)"
                        />
                    </svg>
                </div> */}

                <div className={styles.contentWrapper}>
                    <div className={styles.personSection}>
                        <h2 ref={personRef} className={styles.leftSectionTitle}>
                            {leftSectionTitle}
                        </h2>
                        <h2 ref={vipName} className={styles.vipName}>
                            {vip}
                        </h2>
                    </div>

                    <div className={styles.rightContent}>
                        <div ref={videoContainerRef} className={styles.videoSection}>
                            {/* <blockquote ref={videoQuoteRef} className={styles.videoQuote}>
                                "{quote}"
                            </blockquote> */}
                            <div className={styles.videoPlayer}>
                                <div ref={videoPlayerRef} id="hero-video-player"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Hero;
